import type { IEvent } from 'api/interfaces/events/interfaces.model'
import { Button } from 'components/atoms'
import { STechSupLink } from 'pages/error/style/error.styled'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { durationAPI, formEducationAPI } from '../../../../../axios/axios'
import {
  SAdditionalInfoContainer,
  SAdditionDescription,
  SDescriptionTitle,
  SDescriptionTitleBold,
  SDownloadWrapEvent,
  SInfoContainer,
  SItem,
  SLayout,
  SMainDescription,
  SMainInfoContainer,
  SOrganizatorInfoWrapp,
  SSButton,
} from '../../../../styles/pages.styled'
import {
  DeclarationsENUM,
  DurationsENUM,
  FormEducationENUM,
} from './FormEducation.model'

interface IAboutProps {
  selectedEvent: IEvent
}

const getFileType = (contentType: string) => {
  if (contentType.includes('wordprocessingml')) return 'DOCX'
  if (contentType.includes('spreadsheetml')) return 'XLSX'
  if (contentType.includes('pdf')) return 'PDF'
  return 'Файл'
}

export const About: FC<IAboutProps> = ({ selectedEvent }) => {
  const {
    activity_main_info: activityMainInfo,
    description,
    teachers,
    result,
    purpose,
    cost,
  } = selectedEvent

  const [formEduData, setFormEduData] = useState<any>([])
  const [durationData, setDurationData] = useState<string>('')
  const [planFileLabel, setPlanFileLabel] = useState('')
  const eventPlanUrl = selectedEvent.event_plan_file
    ? new URL(
        selectedEvent.event_plan_file,
        process.env.NEXT_PUBLIC_REACT_APP_URL,
      ).toString()
    : null
  const {
    direction_name: directionName,
    profile,
    edu_form: eduForm,
    adaptation_program: adaptationProgram,
    organizator,
    duration_dimension: durationDimension,
    duration_value: durationValue,
  } = activityMainInfo

  const organizatorEvent = organizator.unit_name ?? 'Нет'

  const profileEvent = profile ? profile.name : 'Нет'

  const adaptation = adaptationProgram ? 'Да' : 'Нет'

  const getDurations = useCallback(async () => {
    const res = await durationAPI.duration()
    const { data } = res.data
    const typesDurations = Object.keys(data).find((key) => {
      if (data[key] === durationDimension) {
        return key
      }
      return null
    })
    const duration = DurationsENUM[typesDurations as keyof typeof DurationsENUM]
    setDurationData(duration)
  }, [durationDimension])

  const declarationOfDuration = (declaration: number, textForms: string[]) => {
    const declarationAfterAbsolute = Math.abs(declaration) % 100
    const number = declarationAfterAbsolute % 10
    if (declarationAfterAbsolute > 10 && declarationAfterAbsolute < 20) {
      return `${declaration} ${textForms[2]}`
    }
    if (number > 1 && number < 5) {
      return `${declaration} ${textForms[1]}`
    }
    if (number === 1) {
      return `${declaration} ${textForms[0]}`
    }
    return `${declaration} ${textForms[2]}`
  }

  const durationWithDeclaration = useMemo(() => {
    switch (durationData) {
      case 'минут':
        return declarationOfDuration(
          durationValue,
          DeclarationsENUM.minutesDeclaration,
        )
      case 'чаcа':
        return declarationOfDuration(
          durationValue,
          DeclarationsENUM.hourDeclaration,
        )
      case 'дня':
        return declarationOfDuration(
          durationValue,
          DeclarationsENUM.dayDeclaration,
        )
      case 'недели':
        return declarationOfDuration(
          durationValue,
          DeclarationsENUM.weekDeclaration,
        )
      case 'месяца':
        return declarationOfDuration(
          durationValue,
          DeclarationsENUM.monthDeclaration,
        )
      case 'года':
        return declarationOfDuration(
          durationValue,
          DeclarationsENUM.yearDeclaration,
        )
      default:
        return null
    }
  }, [durationData, durationValue])

  useEffect(() => {
    getDurations()
  }, [getDurations])

  const upperCaseToString = (string: string) =>
    string.charAt(0).toUpperCase() + string.slice(1)

  const getFormEducation = useCallback(async () => {
    const res = await formEducationAPI.formEdu()
    const { data } = res.data
    const typesForm = Object.keys(data).find((key) => {
      if (data[key] === eduForm) {
        return key
      }
      return null
    })
    const FormEducationEnum =
      FormEducationENUM[typesForm as keyof typeof FormEducationENUM]
    setFormEduData(FormEducationEnum)
  }, [eduForm])

  useEffect(() => {
    getFormEducation()
  }, [getFormEducation])

  useEffect(() => {
    if (!eventPlanUrl) return undefined
    const controller = new AbortController()
    fetch(eventPlanUrl, { method: 'HEAD', signal: controller.signal })
      .then((response) => {
        if (!response.ok) return
        const contentType = response.headers.get('Content-Type') || ''
        const fileType = getFileType(contentType)
        const contentLength = Number(response.headers.get('Content-Length'))
        const size = contentLength
          ? `${new Intl.NumberFormat('ru-RU', {
              maximumFractionDigits: 1,
            }).format(contentLength / 1024)} КБ`
          : ''
        setPlanFileLabel([fileType, size].filter(Boolean).join(' · '))
      })
      .catch((error) => {
        if (!controller.signal.aborted) console.error(error)
      })
    return () => controller.abort()
  }, [eventPlanUrl])

  const teachersNames = useMemo(
    () =>
      teachers.map((teacher) => (
        <SAdditionDescription
          key={`${teacher.teacher_name}_${teacher.position}`}>
          {teacher.teacher_name}
        </SAdditionDescription>
      )),
    [teachers],
  )

  return (
    <SLayout>
      <SMainInfoContainer>
        <SMainDescription>
          <SDescriptionTitleBold>Описание</SDescriptionTitleBold>
          <SAdditionDescription>
            {upperCaseToString(description)}
          </SAdditionDescription>
        </SMainDescription>
        <SAdditionalInfoContainer>
          <SInfoContainer>
            <SItem>
              <SDescriptionTitle>Преподаватели</SDescriptionTitle>
              {teachersNames}
            </SItem>
            <SItem>
              <SDescriptionTitle>Цель проведения</SDescriptionTitle>
              <SAdditionDescription>
                {upperCaseToString(purpose)}
              </SAdditionDescription>
            </SItem>
            <SItem>
              <SDescriptionTitle>Ожидаемый результат</SDescriptionTitle>
              <SAdditionDescription>
                {upperCaseToString(result)}
              </SAdditionDescription>
            </SItem>
          </SInfoContainer>
        </SAdditionalInfoContainer>
      </SMainInfoContainer>
      <SOrganizatorInfoWrapp>
        <SItem>
          <SDescriptionTitle>Направленность</SDescriptionTitle>
          <SAdditionDescription>{directionName}</SAdditionDescription>
        </SItem>
        <SItem>
          <SDescriptionTitle>Профиль</SDescriptionTitle>
          <SAdditionDescription>{profileEvent}</SAdditionDescription>
        </SItem>
        <SItem>
          <SDescriptionTitle>Форма обучения</SDescriptionTitle>
          <SAdditionDescription>{formEduData}</SAdditionDescription>
        </SItem>
        <SItem>
          <SDescriptionTitle>Продолжительность</SDescriptionTitle>
          <SAdditionDescription>{durationWithDeclaration}</SAdditionDescription>
        </SItem>
        <SItem>
          <SDescriptionTitle>Организатор</SDescriptionTitle>
          <STechSupLink href={organizator.site} target="_blank">
            {organizatorEvent}
          </STechSupLink>
        </SItem>
        <SItem>
          <SDescriptionTitle>Стоимость</SDescriptionTitle>
          <SAdditionDescription>{cost}</SAdditionDescription>
        </SItem>
        <SItem>
          <SDescriptionTitle>Предназначен для детей с ОВЗ</SDescriptionTitle>
          <SAdditionDescription>{adaptation}</SAdditionDescription>
        </SItem>
        {eventPlanUrl && (
          <SItem>
            <SSButton>
              <a href={eventPlanUrl}>
                <Button buttonStyle="additional">
                  Скачать план мероприятия
                </Button>
              </a>
            </SSButton>
            {planFileLabel && (
              <SDownloadWrapEvent>{planFileLabel}</SDownloadWrapEvent>
            )}
          </SItem>
        )}
      </SOrganizatorInfoWrapp>
    </SLayout>
  )
}
